'use client';

import React, { useMemo } from 'react';
import { ClassNamesConfig, SingleValue } from 'react-select';
import SelectInput from '@/app/_components/select/select';
import { Language } from '@/app/_lib/interfaces';

type Option = {
  value: string;
  label: string;
};

interface LanguageSelectProps {
  languages: Language[];
  value: string;
  onChange: (languageCode: string) => void;
  disabled?: boolean;
  disableSearching?: boolean;
  isLanguagesLoading?: boolean;
  className?: string;
  classNames?: ClassNamesConfig;
  variant: 'default' | 'primary';
}

const LanguageSelect: React.FC<LanguageSelectProps> = ({
  languages,
  value,
  onChange,
  disabled,
  disableSearching,
  isLanguagesLoading,
  className,
  classNames,
  variant,
}) => {
  const options: Option[] = useMemo(() => {
    return languages
      ?.sort((a, b) => a.name.localeCompare(b.name))
      ?.map((lang) => ({ value: lang.code, label: lang.name }));
  }, [languages]);

  const selectedOption: Option | undefined = useMemo(() => {
    return options?.find((option) => option.value === value);
  }, [options, value]);

  const handleChange = (selectedOption: SingleValue<Option>) => {
    if (selectedOption) {
      onChange(selectedOption.value);
    }
  };

  return (
    <div data-test-id="language-select">
      <SelectInput
        value={selectedOption}
        onChange={handleChange}
        options={options}
        isSearchable={!disableSearching}
        isClearable={false}
        isLoading={isLanguagesLoading}
        isDisabled={disabled}
        placeholder="Language"
        className={className}
        classNames={classNames}
        variant={variant}
      />
    </div>
  );
};

export default LanguageSelect;
